import { useCallback, useEffect, useRef, useState } from "react";
import {
  exportLeaderboardAsync,
  getLeaderboardAsync,
  getPlayerRankAsync,
  InsertManualScoreAdjustmentAsync,
} from "../../utils/api";
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Navbar,
  Row,
  Spinner,
  Table,
  Modal,
} from "react-bootstrap";
import moment from "moment";
import Pagination from "../../components/Pagination";
import {
  downloadBlob,
  formatNumber,
  getTournamentBonusDisplayText,
  isNullOrWhiteSpace,
} from "../../utils/helpers";
import React from "react";
import constants from "../../utils/constants";
import { XLg } from "react-bootstrap-icons";
import Textarea from "../../components/Textarea";
import Input from "../../components/Input";
import NumberInput from "../../components/NumberInput";

const page_size = 100;
const search_delay = 1000;

const Leaderboard = ({ tournament, setToasts }) => {
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const ctsRef = useRef(null);

  const [refresh, setRefresh] = useState(0);

  const [state, setState] = useState({
    loading: true,
    exporting: false,
    leaderboard: {},
    page: 0,
    error: null,
  });

  const [manualScoreAdjustmentModalState, setManualScoreAdjustmentModalState] =
    useState({
      show: false,
      user_id: "",
      tournament_id: tournament.id,
      score: 0,
      note: "",
      errors: [],
    });

  const handleExport = useCallback(async () => {
    setState((prev) => ({ ...prev, exporting: true }));

    try {
      const blob = await exportLeaderboardAsync(tournament.id);
      const now = new Date();
      const filename = `${tournament.id}_leaderboard_${now.toISOString()}.csv`;

      downloadBlob(blob, filename);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }

    setState((prev) => ({ ...prev, exporting: false }));
  }, [tournament.id]);

  const handleSearch = (e) => {
    ctsRef.current?.abort();
    ctsRef.current = new AbortController();

    const searchString = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      if (!isNullOrWhiteSpace(searchString)) {
        search(searchString, ctsRef.current.signal);
      } else {
        fetchData(ctsRef.current.signal);
      }
    }, search_delay);
  };

  const handleClearSearch = () => {
    if (isNullOrWhiteSpace(searchRef.current.value)) return;

    searchRef.current.value = "";
    searchRef.current.focus();

    handleSearch({ target: { value: "" } });
  };

  const handleFetchData = () => {
    setState((prev) => ({ ...prev, loading: true }));

    ctsRef.current?.abort();
    ctsRef.current = new AbortController();

    const searchString = searchRef.current.value;

    if (!isNullOrWhiteSpace(searchString)) {
      search(searchString, ctsRef.current.signal);
    } else {
      fetchData(ctsRef.current.signal);
    }
  };

  const handlePageClick = useCallback((page) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const search = async (searchString, ct) => {
    try {
      const rank = await getPlayerRankAsync(tournament.id, searchString, ct);

      if (rank === null) {
        setState((prev) => ({
          ...prev,
          loading: false,
          leaderboard: {},
          error: null,
        }));

        return;
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        leaderboard: { size: 1, rows: [{ ...rank.row, rank: rank.rank }] },
        error: null,
      }));
    } catch (error) {
      if (ct?.aborted) return;

      setState((prev) => ({
        ...prev,
        loading: false,
        leaderboard: {},
        error: error,
      }));
    }
  };

  const fetchData = async (ct) => {
    try {
      const leaderboard = await getLeaderboardAsync(
        tournament.id,
        page_size,
        state.page * page_size,
        ct,
      );

      setState((prev) => ({
        ...prev,
        loading: false,
        leaderboard,
        error: null,
      }));
    } catch (error) {
      if (ct?.aborted) return;

      setState((prev) => ({
        ...prev,
        loading: false,
        leaderboard: {},
        error: error,
      }));
    }
  };

  const handleManualScoreAdjustmentModalShow = (user_id) => {
    setManualScoreAdjustmentModalState((prev) => ({
      ...prev,
      user_id,
      show: true,
      score: 0,
      note: "",
      errors: {},
    }));
  };

  const handleManualScoreAdjustmentModalHide = () => {
    setManualScoreAdjustmentModalState((prev) => ({ ...prev, show: false }));
  };

  const handleManualScoreAdjustmentModalChange = (e) => {
    const { name, value } = e.target;
    setManualScoreAdjustmentModalState((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[name]; // Remove the error for the changed field

      return {
        ...prev,
        [name]: value,
        errors: newErrors,
      };
    });
  };

  const validateManualScoreAdjustmentModal = () => {
    const errors = {};
    if (!manualScoreAdjustmentModalState.note) {
      errors.note = ["Note is mandatory"];
    }
    if (!manualScoreAdjustmentModalState.score) {
      errors.score = ["Score is mandatory"];
    }
    if (!manualScoreAdjustmentModalState.user_id) {
      errors.user_id = ["User ID is mandatory"];
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleManualScoreAdjustmentModalSubmit = async () => {
    ctsRef.current = new AbortController();

    var errors = validateManualScoreAdjustmentModal();

    if (errors) {
      setManualScoreAdjustmentModalState((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      await InsertManualScoreAdjustmentAsync(
        manualScoreAdjustmentModalState,
        ctsRef.current.signal,
      );

      setToasts((prev) => [
        ...prev,
        { header: "Success", text: "Adjustment saved" },
      ]);
    } catch {
      setToasts((prev) => [
        ...prev,
        { header: "Error", text: "Failed to adjust score" },
      ]);
    }

    setRefresh((prev) => prev + 1);

    setManualScoreAdjustmentModalState((prev) => ({ ...prev, show: false }));
  };

  useEffect(() => {
    handleFetchData();

    return () => {
      ctsRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, state.page]);

  const pages = state.leaderboard.size
    ? Math.ceil(state.leaderboard.size / page_size)
    : 0;

  const prizes = [];
  tournament.tiers.forEach((tier) => {
    const bonusCode = tier.bonus.code;
    const bonusDisplayText = getTournamentBonusDisplayText(tier.bonus);
    for (let i = tier.rank_min; i <= tier.rank_max; i++) {
      prizes[i] ??= [];
      prizes[i].push({
        code: bonusCode,
        displayText: bonusDisplayText,
      });
    }
  });

  const pageIdx = Math.max(state.page, 0);
  const lastPageIdx = Math.max(pages - 1, 0);

  const hasFinishScore =
    tournament.finish_score != null &&
    tournament.finish_score !== "" &&
    Number(tournament.finish_score) > 0;
  const tableColSpan = hasFinishScore ? 8 : 7;

  return (
    <div className="leaderboard">
      <div className="mb-2">
        <small>
          {state.leaderboard.size ?? 0} rows, {page_size} rows per page, {pages}{" "}
          pages
        </small>
      </div>

      <Row className="align-items-center mb-2">
        <Col lg={4} md={6} xs={8}>
          <InputGroup>
            <Form.Control
              type="text"
              onChange={handleSearch}
              ref={searchRef}
              placeholder="Search..."
            />
            <Button
              variant="outline-secondary"
              className="d-flex align-items-center p-2"
              onClick={handleClearSearch}
            >
              <XLg />
            </Button>
          </InputGroup>
        </Col>

        <Col className="text-end">
          {tournament.status === constants.Tournament.Status.InProgress && (
            <Button
              variant="outline-primary"
              className="me-2"
              size="sm"
              onClick={() => handleManualScoreAdjustmentModalShow("")}
              disabled={state.loading}
            >
              Add score
            </Button>
          )}
          <Button
            variant="outline-success"
            className="me-2"
            size="sm"
            onClick={handleExport}
            disabled={state.exporting}
          >
            {state.exporting ? (
              <React.Fragment>
                <Spinner size="sm" className="me-2" /> exporting...
              </React.Fragment>
            ) : (
              "Export"
            )}
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setRefresh((prev) => prev + 1)}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Table className="mt-3" bordered responsive hover>
        <thead>
          <tr>
            <th rowSpan={2}>Rank</th>
            <th colSpan={2}>User</th>
            <th rowSpan={2}>Score</th>
            <th rowSpan={2}>Computed</th>
            {hasFinishScore && <th rowSpan={2}>Reached finish score</th>}
            <th colSpan={2}>Prize</th>
          </tr>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Code</th>
            <th>Name</th>
          </tr>
        </thead>

        <tbody>
          {state.loading ? (
            <tr>
              <td colSpan={tableColSpan}>Loading...</td>
            </tr>
          ) : state.error ? (
            <tr>
              <td colSpan={tableColSpan}>Error: {state.error.message}</td>
            </tr>
          ) : state.leaderboard.rows?.length > 0 ? (
            state.leaderboard.rows?.map((row) => {
              const computed = moment.utc(row.computed).local();
              const computedStr = computed.format(
                constants.DateTimeStringFormat,
              );

              const computedFromNowStr = computed.fromNow();

              const reachedFinishScoreAt =
                row.reached_finish_score_at != null
                  ? moment.utc(row.reached_finish_score_at).local()
                  : null;
              const reachedFinishScoreAtStr = reachedFinishScoreAt
                ? reachedFinishScoreAt.format("DD MMM YYYY, HH:mm:ss.SSS")
                : null;
              const reachedFinishScoreAtFromNowStr = reachedFinishScoreAt
                ? reachedFinishScoreAt.fromNow()
                : null;

              const prize = prizes[row.rank];
              const prizeCodes = [];
              const prizeDisplayTexts = [];
              if (prize) {
                prize.forEach((p) => {
                  prizeCodes.push(p.code);
                  prizeDisplayTexts.push(p.displayText);
                });
              }

              const hasPrize = prizeCodes.length > 0;

              return (
                <tr
                  key={row.rank}
                  className={hasPrize ? "leaderboard-row-prize" : undefined}
                >
                  <th scope="row">{row.rank}</th>
                  <td>{row.user_id}</td>
                  <td>{row.user_name}</td>
                  <td>
                    {formatNumber(Math.round(row.total_score))}
                    {tournament.status ===
                      constants.Tournament.Status.InProgress && (
                      <Button
                        variant="outline-primary"
                        className="float-end"
                        size="sm"
                        onClick={() =>
                          handleManualScoreAdjustmentModalShow(row.user_id)
                        }
                        disabled={state.loading}
                      >
                        +
                      </Button>
                    )}
                  </td>
                  <td>
                    {computedStr}
                    <small className="d-block text-muted">
                      {computedFromNowStr}
                    </small>
                  </td>
                  {hasFinishScore && (
                    <td>
                      {reachedFinishScoreAtStr ?? "-"}
                      {reachedFinishScoreAtFromNowStr && (
                        <small className="d-block text-muted">
                          {reachedFinishScoreAtFromNowStr}
                        </small>
                      )}
                    </td>
                  )}
                  <td>{prizeCodes.length ? prizeCodes.join(", ") : "-"}</td>
                  <td>
                    {prizeDisplayTexts.length
                      ? prizeDisplayTexts.join(", ")
                      : "-"}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={tableColSpan}>No data</td>
            </tr>
          )}
        </tbody>
      </Table>
      <Modal
        show={manualScoreAdjustmentModalState.show}
        onHide={handleManualScoreAdjustmentModalHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>Adjust Score</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Input
              className="mb-3"
              label="User ID"
              name="user_id"
              value={manualScoreAdjustmentModalState.user_id}
              onChange={handleManualScoreAdjustmentModalChange}
              errorMessages={manualScoreAdjustmentModalState.errors?.user_id}
            />

            <NumberInput
              className="mb-3"
              label="Score"
              name="score"
              value={manualScoreAdjustmentModalState.score}
              onChange={handleManualScoreAdjustmentModalChange}
              errorMessages={manualScoreAdjustmentModalState.errors?.score}
            />

            <Textarea
              className="mb-3"
              label="Note"
              name="note"
              value={manualScoreAdjustmentModalState.note}
              onChange={handleManualScoreAdjustmentModalChange}
              placeholder="Enter notes..."
              errorMessages={manualScoreAdjustmentModalState.errors?.note}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleManualScoreAdjustmentModalHide}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleManualScoreAdjustmentModalSubmit}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {pages > 2 && (
        <Navbar className="fixed-bottom bg-body-tertiary border-top p-3">
          <Container fluid>
            <Pagination
              className="m-0"
              page={pageIdx}
              maxPage={lastPageIdx}
              margin={3}
              onPageClick={handlePageClick}
            />
          </Container>
        </Navbar>
      )}
    </div>
  );
};

export default Leaderboard;
